import { FormEvent, useEffect, useState } from "react";

const styles = {
  page: {
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 15,
    lineHeight: 1.7,
    color: "#3a3a3a",
    minHeight: "100vh",
    backgroundColor: "#ffffff",
  } as const,
  container: {
    maxWidth: 800,
    margin: "0 auto",
    padding: "40px 20px",
  } as const,
  logo: {
    maxHeight: 60,
  } as const,
  heading: {
    fontFamily: "Georgia, serif",
    fontSize: 30,
    fontWeight: "bold" as const,
    lineHeight: "48px",
    color: "#3a3a3a",
    textAlign: "center" as const,
    margin: "30px 0 20px",
  },
  paragraph: {
    textAlign: "center" as const,
    marginBottom: 30,
  },
  label: {
    display: "block",
    marginBottom: 4,
    fontWeight: 600,
  } as const,
  input: {
    width: "100%",
    border: "3px solid #e4e4e4",
    borderRadius: 0,
    padding: "4px 6px",
    lineHeight: "26px",
    fontSize: 15,
    fontFamily: "'Open Sans', sans-serif",
    backgroundColor: "#ffffff",
    boxSizing: "border-box" as const,
    outline: "none",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 4,
  } as const,
  checkbox: {
    marginTop: 5,
    accentColor: "#3a3a3a",
  } as const,
  button: {
    backgroundColor: "#edb723",
    color: "#3a3a3a",
    fontSize: 15,
    fontFamily: "'Open Sans', sans-serif",
    padding: "10px 25px",
    border: "none",
    borderRadius: 0,
    cursor: "pointer",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  } as const,
  footer: {
    borderTop: "1px solid #e4e4e4",
    marginTop: 50,
    paddingTop: 30,
    textAlign: "center" as const,
    fontSize: 13,
    color: "#3a3a3a",
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: 16,
    flexWrap: "wrap" as const,
    marginTop: 12,
  },
  footerLink: {
    color: "#346ca5",
    textDecoration: "none",
    fontSize: 13,
  },
  socialIcons: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
    marginBottom: 16,
  },
};

function Cancellation() {
  const [email, setEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const bordesUrl = "https://www.bordes.be";

  useEffect(() => {
    document.title = "Annuleer je afspraak";
  }, []);

  useEffect(() => {
    if (!cancelled) return;
    if (countdown <= 0) {
      window.location.href = bordesUrl;
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cancelled, countdown]);

  const urlParams = new URLSearchParams(window.location.search);
  const location = urlParams.get("adres") ?? "";
  const appointmentDate = (urlParams.get("tijd") ?? "").replace(/@/g, "");
  const logoUrl = "https://storage.googleapis.com/automation-cdn/bordes/logo.png";

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  const taskId = urlParams.get("id") ?? "";
  const taskStartDate = urlParams.get("timestamp") ?? "";
  const colibryHost = (window as any)._env_?.REACT_APP_COLIBRY_SERVICES_HOST ?? "";
  const customerId = "UnL37Y8e";
  const apiKey = "AIzaSyDh3w116brneR1WGXDYeuSdUNGU21Xkjmw";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!confirmed || !isValidEmail) return;

    const formData = new URLSearchParams();
    formData.append("task_id", taskId);
    formData.append("task_start_date", taskStartDate);
    formData.append("email", email);

    await fetch(
      `${colibryHost}/api/2/automation/cancel-automation-task/_post?customer_id=${customerId}&key=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: formData,
      },
    );

    setCancelled(true);
  }

  const showEmailWarning = submitted && !isValidEmail;
  const showConfirmWarning = submitted && !confirmed;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div
          style={{
            backgroundColor: "#0f0f3b",
            textAlign: "center",
            padding: "20px 0",
            marginBottom: 10,
          }}
        >
          <img src={logoUrl} alt="Logo" style={styles.logo} />
        </div>

        {cancelled ? (
          <>
            <h1 style={styles.heading}>Je afspraak is geannuleerd</h1>
            <p style={styles.paragraph}>Bedankt om je afspraak te annuleren.</p>
            <div style={{ textAlign: "center" }}>
              <a href={bordesUrl} style={{ ...styles.button, textDecoration: "none", display: "inline-block" }}>
                Ga terug naar de Bordes website ({countdown})
              </a>
            </div>
          </>
        ) : (
          <>
            <h1 style={styles.heading}>
              Annuleer je afspraak te
              <br />
              {location}
            </h1>

            <p style={styles.paragraph}>
              Om de afspraak <strong>{appointmentDate}</strong> te annuleren, verzoeken we je vriendelijk om je
              e-mailadres in te voeren ter verificatie.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>
                  Email<span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="email"
                  style={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {showEmailWarning && (
                  <div style={{ color: "red", fontSize: 13, marginTop: 4 }}>
                    Gelieve een geldig e-mailadres in te voeren.
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={styles.label}>
                  Bevestiging<span style={{ color: "red" }}>*</span>
                </label>
                <div style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    id="confirmCancel"
                    style={styles.checkbox}
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    required
                  />
                  <label htmlFor="confirmCancel">Ik bevestig dat ik mijn afspraak wil annuleren</label>
                </div>
                {showConfirmWarning && (
                  <div style={{ color: "red", fontSize: 13, marginTop: 4 }}>
                    Gelieve te bevestigen dat je je afspraak wil annuleren.
                  </div>
                )}
              </div>

              <div style={{ textAlign: "center" }}>
                <button
                  type="submit"
                  style={styles.button}
                >
                  Annuleer je afspraak
                </button>
              </div>
            </form>
          </>
        )}

        <div style={styles.footer}>
          <img src={logoUrl} alt="Logo" style={{ maxHeight: 40 }} />
          <div style={styles.socialIcons}>
            <a
              href="https://www.facebook.com/bordesbv"
              aria-label="Facebook"
              style={{ color: "#346ca5", fontSize: 20 }}
            >
              <i className="bi bi-facebook"></i>
            </a>
            <a
              href="https://www.linkedin.com/company/bordes/"
              aria-label="LinkedIn"
              style={{ color: "#346ca5", fontSize: 20 }}
            >
              <i className="bi bi-linkedin"></i>
            </a>
            <a
              href="https://www.instagram.com/bordes_vastgoed/"
              aria-label="Instagram"
              style={{ color: "#346ca5", fontSize: 20 }}
            >
              <i className="bi bi-instagram"></i>
            </a>
          </div>
          <div style={styles.footerLinks}>
            <a href="https://www.bordes.be/algemene-voorwaarden" style={styles.footerLink}>
              Algemene voorwaarden
            </a>
            <a href="https://www.bordes.be/disclaimer" style={styles.footerLink}>
              Disclaimer
            </a>
            <a href="https://www.bordes.be/privacy-policy" style={styles.footerLink}>
              Privacy Policy
            </a>
            <a href="https://www.bordes.be/cookie-policy" style={styles.footerLink}>
              Cookie Policy
            </a>
          </div>
          <p style={{ marginTop: 16, fontSize: 12, color: "#999" }}>
            &copy; {new Date().getFullYear()} Bordes. Alle rechten voorbehouden.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Cancellation;
